<template>
    <div class="file-manager" 
         @dragover.prevent="handleDragOver"
         @dragleave.prevent="handleDragLeave"
         @drop.prevent="handleDrop">
      <!-- 顶部操作栏 -->
      <div class="file-manager-header">
        <div class="breadcrumb">
          <el-breadcrumb separator=">">
            <el-breadcrumb-item  @click.native="navigateToFolder(-1)">根目录</el-breadcrumb-item>
            <el-breadcrumb-item 
              v-for="(folder, index) in currentPath" 
              :key="index"
              @click.native="navigateToFolder(index)"
            >
            <i class="el-icon-folder"></i>  {{ folder.name }}
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        
        <div class="actions" >
          <el-dropdown split-button type="primary" size="small" @click="showUploadDialog" v-if="!pre">
            <i class="el-icon-upload"></i> 上传文件
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="showUploadDialog">上传文件</el-dropdown-item>
              <el-dropdown-item @click.native="triggerFolderUpload">上传文件夹</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button type="success" size="small" @click="showNewFolderDialog" v-if="!pre">
            <i class="el-icon-folder-add"></i> 新建文件夹
          </el-button>
          <el-button
            type="warning" 
            size="small" 
            @click="batchDownload"
            :disabled="selectedFiles.length === 0"
          >
            <i class="el-icon-download"></i> 下载 ({{ selectedFiles.length }})
          </el-button>
        </div>
      </div>
      
      <!-- 文件列表区域 -->
      <div class="file-list-container" :class="{ 'drag-over': isDragOver }">
        <!-- 拖拽提示层 -->
        <div class="drag-overlay" v-show="isDragOver">
          <div class="drag-tip">
            <i class="el-icon-upload"></i>
            <p>释放鼠标以上传文件或文件夹</p>
          </div>
        </div>
        
        <!-- 加载中 -->
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>
        
        <!-- 空状态 -->
        <el-empty v-else-if="fileList.length === 0 &&!pre" description="暂无文件，可以拖拽文件到此上传">
          <el-button type="primary" @click="showUploadDialog">上传文件</el-button>
        </el-empty>
        
        <!-- 文件列表 -->
        <el-table
          v-else
          :data="fileList"
          style="width: 100%"
          @row-click="handleRowDblClick"
          @selection-change="handleSelectionChange"
          ref="fileTable"
        >
          <el-table-column 
            type="selection" 
            width="55"
            :selectable="checkSelectable"
          >
          </el-table-column>
          <el-table-column label="名称" >
            <template slot-scope="scope">
              <div class="file-item">
                <img  v-if="scope.row.extension=='pdf'"
                      src="https://www.imgbed.link/file/24751">
                <img
                    v-else-if="scope.row.extension=='docx' || scope.row.extension=='doc'"
                    src="https://pan.imgbed.link/public/newimg/icon-docx.png"
                    mode="aspectFit"
                />
                <img
                    v-else-if="scope.row.extension=='pptx' || scope.row.extension=='ppt'"
                    src="https://pan.imgbed.link/public/newimg/icon-ppt.png"
                    mode="aspectFit"
                />
                <img
                    v-else-if="scope.row.extension=='xls' || scope.row.extension=='xlsx'"
                    src="https://pan.imgbed.link/public/newimg/icon-xlsx.png"
                />
                <i v-else :class="getFileIcon(scope.row)"></i>
                <span class="file-name" :title="scope.row.name">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="size" label="大小" width="80px">
            <template slot-scope="scope">
              {{!scope.row.isFolder? formatFileSize(scope.row.size):'' }}
            </template>
          </el-table-column>
          <el-table-column  label="创建人" width="80px">
            <template slot-scope="scope">
              {{scope.row.imUserVo.nickName}}
            </template>
          </el-table-column>
          <el-table-column prop="updateTime" label="修改日期" >
            <template slot-scope="scope">
              {{ scope.row.updateTime || scope.row.createTime }}
            </template>
          </el-table-column>
          
          <el-table-column label="操作" fixed="right">
            <template slot="header" >
              <el-input
                  v-model="search"
                  size="mini"
                  prefix-icon="el-icon-search"
                  clearable
                  @input="fetchFileList"
                  placeholder="输入关键字搜索"/>
            </template>
            <template slot-scope="scope">
              <el-button
                v-if="!scope.row.isFolder"
                size="mini"
                type="text"
                @click.stop="handlePreview(scope.row)"
              >
                预览
              </el-button>
              <el-button
                v-if="!scope.row.isFolder"
                size="mini"
                type="text"
                @click.stop="downloadFile(scope.row)"
              >
                下载
              </el-button>
              <el-button  v-if="!pre"
                size="mini"
                type="text"
                @click.stop="renameFile(scope.row)"
              >
                重命名
              </el-button>
              <el-button v-if="!pre"
                size="mini"
                type="text"
                class="delete-btn"
                @click.stop="deleteFile(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页控件 -->
        <div class="pagination-container" v-if="total > pageSize">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            :current-page="pageNum"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
      
      <!-- 上传文件对话框 -->
      <el-dialog
        title="上传文件"
        :visible.sync="uploadDialogVisible"
        width="500px"
        :z-index="4000"
        :modal="false"
      >
        <el-upload
          class="upload-demo"
          drag
          :action="uploadUrl"
          :data="uploadData"
          :headers="uploadHeaders"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :multiple="true"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
          <div class="el-upload__tip" slot="tip">支持各种类型的文件</div>
        </el-upload>
      </el-dialog>
      
      <!-- 隐藏的文件夹上传输入 -->
      <input 
        ref="folderUploadInput" 
        type="file" 
        webkitdirectory 
        directory 
        multiple 
        style="display: none" 
        @change="handleFolderUpload" 
      />
      
      <!-- 新建文件夹对话框 -->
      <el-dialog
        title="新建文件夹"
        :visible.sync="newFolderDialogVisible"
        width="400px"
        :z-index="4000"
        :modal="false"
      >
        <el-form :model="newFolderForm" label-width="80px">
          <el-form-item label="文件夹名">
            <el-input v-model="newFolderForm.name" placeholder="请输入文件夹名称"></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="newFolderDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="createNewFolder">确 定</el-button>
        </div>
      </el-dialog>
      
      <!-- 重命名对话框 -->
      <el-dialog
        title="重命名"
        :visible.sync="renameDialogVisible"
        width="400px"
        :z-index="4000"
        :modal="false"
      >
        <el-form :model="renameForm" label-width="80px">
          <el-form-item label="新名称">
            <el-input v-model="renameForm.name" placeholder="请输入新名称"></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="renameDialogVisible = false">取 消</el-button>
          <el-button type="primary" @click="confirmRename">确 定</el-button>
        </div>
      </el-dialog>
      
      <!-- 上传进度条 - 可拖动和调整大小 -->
      <div 
        v-if="showUploadProgress"
        :class="['upload-progress-container', {'minimized': isProgressMinimized}]"
        :style="isProgressMinimized ? 
          { position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 } : 
          { 
            width: progressWidth + 'px', 
            height: progressHeight + 'px',
            left: progressLeft + 'px',
            top: progressTop + 'px',
            bottom: 'auto',
            right: 'auto',
            position: 'fixed',
            zIndex: 9999
          }"
        ref="progressContainer"
      >
        <!-- 最小化状态 -->
        <div v-if="isProgressMinimized" class="progress-minimized" @click="toggleMinimize">
          <el-progress type="circle" :percentage="totalProgress" ></el-progress>
        </div>
        
        <!-- 正常状态 -->
        <template v-else>
          <div 
            class="upload-progress-header"
            @mousedown="startDrag"
          >
            <div class="progress-title">
              <h3>文件上传进度</h3>
              <span class="progress-summary">{{ getCompletedCount() }}/{{ Object.keys(uploadProgress).length }} 已完成</span>
            </div>
            <div class="progress-actions">
              <el-button type="text" icon="el-icon-minus" @click="toggleMinimize" title="最小化"></el-button>
              <el-button type="text" icon="el-icon-delete" @click="clearUploadProgress" title="清空列表"></el-button>
              <el-button type="text" icon="el-icon-close" @click="showUploadProgress = false" title="关闭"></el-button>
            </div>
          </div>
          <div class="upload-progress-list">
            <div v-for="(progress, fileName) in uploadProgress" :key="fileName" class="upload-progress-item">
              <div class="file-info">
                <span class="file-name" :title="fileName" >{{ fileName }}</span>
                <span>{{ formatFileSize(progress.file.size) }}</span>
                <!-- 添加重试按钮 -->
                <el-button
                    v-if="progress.status!='success'"
                  type="text"
                  icon="el-icon-refresh" 
                  class="retry-button"
                  @click="retryUpload(fileName, progress)"
                  title="重新上传"
                ></el-button>
              </div>
              <el-progress
                :percentage="progress.percent"
                :status="progress.status"
                :text-inside="true"
                :stroke-width="12"
              ></el-progress>
            </div>
          </div>
          
          <!-- 调整大小的手柄 -->
          <div class="resize-handle resize-handle-se" @mousedown.stop="startResize"></div>
        </template>
      </div>
    </div>
  </template>
  
  <script>
  import { listFiles, uploadFile, createFolder, updateFile, delFile, getDownloadUrl, batchUploadFiles, batchUpdateBusId } from '@/api/file'
  import axios from 'axios'
  
  export default {
    name: 'FileManager',
    props: {
      // 业务ID，用于关联特定业务的文件
      busId: {
        type: [Number,String],
        default: ''
      },
      //业务路径，业务类型+id,比如circle1
      path: {
        type: [Number,String],
        default: ''
      },
      parentId: {
        type: [Number,String],
        default: null
      },
      //预览模式
      pre: {
        type: Boolean,
        default: false
      },
      // 是否需要延迟关联
      needLateBinding: {
        type: Boolean,
        default: false
      }
    },
    computed:{
      totalProgress(){
       return  Math.floor( ( this.getCompletedCount()/ Object.keys(this.uploadProgress).length)*100)
      }
    },
    data() {
      return {
        loading: false,
        fileList: [],
        currentPath: [],
        currentFolderId: 0,
        
        // 上传文件相关
        uploadDialogVisible: false,
        uploadUrl:  process.env["VUE_APP_BASE_API2"]+ '/klgeek/file/upload',
        uploadData: {
          parentId: 0
        },
        uploadHeaders: {
          accessToken: `${sessionStorage.getItem('accessToken')}`
        },
        // 新建文件夹相关
        newFolderDialogVisible: false,
        newFolderForm: {
          name: ''
        },
        
        // 重命名相关
        renameDialogVisible: false,
        renameForm: {
          name: '',
          id: null
        },
        previewFileContent: '',
        // 添加文件夹上传相关数据
        uploadingFolder: false,
        currentFolderPath: '',
        folderFiles: [],
        processedFiles: 0,
        totalFiles: 0,
        isDragOver: false,
        // 分页相关
        search:'',
        pageNum: 1,
        pageSize: 10,
        total: 0,
        pendingFiles: [], // 存储待关联的文件
        uploadProgress: {}, // 存储每个文件的上传进度
        showUploadProgress: false, // 是否显示进度条
        progressLeft: window.innerWidth - 470, // 默认右侧位置
        progressTop: window.innerHeight - 420, // 默认底部位置
        progressWidth: 450,
        progressHeight: 400,
        isDragging: false,
        isResizing: false,
        dragStartX: 0,
        dragStartY: 0,
        dragStartLeft: 0,
        dragStartTop: 0,
        resizeStartWidth: 0,
        resizeStartHeight: 0,
        isProgressMinimized: false,
        selectedFiles: [] // 选中的文件列表
      }
    },
    created() {
      if(this.parentId){
        this.currentFolderId=this.parentId
      }
      this.fetchFileList()
    },
    mounted() {
      // 添加全局鼠标事件监听
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
    },
    beforeDestroy() {
      // 移除全局事件监听
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
    },
    methods: {
      // 获取文件列表
      async fetchFileList() {
        this.loading = true
        this.isDragOver = false
        try {
          const response = await listFiles({
            parentId: this.search?'':this.currentFolderId,
            busId: this.busId,
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            name:this.search,
            path:this.path
          })
          if (response.code === 200) {
            this.fileList = response.rows || []
            this.total = response.total || 0
            if(this.total==0 && (this.currentFolderId==0||this.currentFolderId==this.parentId) && !this.search)
              this.$emit('noData', true);
          } else {
            this.$message.error('获取文件列表失败: ' + response.msg)
          }
        } catch (error) {
          console.error('获取文件列表失败:', error)
          this.$message.error('获取文件列表失败')
        } finally {
          this.search='';
          this.loading = false
        }
      },
      getFileList(){
        this.$emit("fileList",this.fileList)
      },
      // 显示上传对话框
      showUploadDialog() {
        this.uploadData.parentId = this.currentFolderId
        this.uploadData.busId = this.busId
        this.uploadData.path=this.path
        this.uploadDialogVisible = true
      },
      
      // 上传成功回调
      handleUploadSuccess(response, file, fileList) {
        if (this.needLateBinding && response.code === 200) {
          // 存储上传成功的文件ID，等待后续关联
          this.pendingFiles.push(...response.data)
        }
        this.$message.success('上传成功')
        fileList=[];
        this.uploadDialogVisible = false
        this.fetchFileList()
      },
      
      // 上传失败回调
      handleUploadError(err, file, fileList) {
        this.$message.error('上传失败: ' + err)
      },
      
      // 显示新建文件夹对话框
      showNewFolderDialog() {
        this.newFolderForm.name = ''
        this.newFolderDialogVisible = true
      },
      
      // 创建新文件夹
      createNewFolder() {
        if (!this.newFolderForm.name) {
          this.$message.warning('请输入文件夹名称')
          return
        }
        
        createFolder(this.newFolderForm.name, this.currentFolderId,this.busId,this.path).then((response) => {
          if (this.needLateBinding && response.code === 200) {
            // 存储上传成功的文件ID，等待后续关联
            this.pendingFiles.push(response.data.id)
          }
          this.$message.success('创建文件夹成功')
          this.newFolderDialogVisible = false
          this.fetchFileList()
        }).catch(error => {
          this.$message.error('创建文件夹失败: ' + error.message)
        })
      },
      
      // 单击击行处理
      handleRowDblClick(row) {
        if (row.isFolder === 1) {
          // 进入文件夹
          this.pageNum=1;
          this.currentPath.push({
            id: row.id,
            name: row.name
          })
          this.currentFolderId = row.id
          this.fetchFileList()
        } else {
          // 预览文件
          this.handlePreview(row)
        }
      },
      
      // 导航到指定文件夹
      navigateToFolder(index) {
        this.pageNum=1;
        if (index === -1) {
          // 返回根目录
          this.currentPath = []
          this.currentFolderId =0;
          if(this.parentId)
          this.currentFolderId = this.parentId
        } else {
          // 返回到指定层级
          this.currentPath = this.currentPath.slice(0, index + 1)
          this.currentFolderId = this.currentPath[index].id
        }
        this.fetchFileList()
      },
      
      // 处理文件预览
      handlePreview(file) {
     /*   let linkUrl =null;
        if(file.extension=='ppt'||file.extension=='pptx'||file.extension=='doc'||file.extension=='docx'||file.extension=='xls'||file.extension=='pdf'||file.extension=='xlsx')
        {
          const lastSlashIndex = file.url.lastIndexOf('/');
          linkUrl = "https://www.imgbed.link/file/preview/"+file.url.substring(lastSlashIndex + 1);
        }else{
          const encoder = new TextEncoder();
          const data = file.url+"?fullfilename="+file.name;
          const encodedData = btoa(String.fromCharCode(...new Uint8Array(encoder.encode(data))));
          linkUrl ="https://www.imgbed.link/kkpreview/onlinePreview?url="+encodeURIComponent(encodedData)
        }*/
        window.open(file.preUrl)
      },
      
      // 下载文件
     async downloadFile(file) {
        if (file.isFolder === 1) {
          this.$message.warning('文件夹不支持下载')
          return
        }
       window.open(file.url,"_blank")
      },
      
      // 重命名文件或文件夹
      renameFile(file) {
        this.renameForm.name = file.name
        this.renameForm.id = file.id
        this.renameDialogVisible = true
      },
      
      // 确认重命名
      confirmRename() {
        if (!this.renameForm.name) {
          this.$message.warning('请输入名称')
          return
        }
        
        updateFile({
          id: this.renameForm.id,
          name: this.renameForm.name
        }).then(() => {
          this.$message.success('重命名成功')
          this.renameDialogVisible = false
          this.fetchFileList()
        }).catch(error => {
          this.$message.error('重命名失败: ' + error.message)
        })
      },
      
      // 删除文件或文件夹
      deleteFile(file) {
        this.$confirm(`确定要删除${file.isFolder === 1 ? '文件夹' : '文件'} "${file.name}" 吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          delFile(file.id).then(() => {
            this.$message.success('删除成功')
            this.fetchFileList()
          }).catch(error => {
            this.$message.error('删除失败: ' + error.message)
          })
        }).catch(() => {
          // 取消删除
        })
      },
      
      // 获取文件图标
      getFileIcon(file) {
        if (file.isFolder === 1) {
          return 'el-icon-folder'
        }
        
        const extension = file.extension ? file.extension.toLowerCase() : ''
        
        if (this.isImage({ extension })) {
          return 'el-icon-picture'
        } else if (this.isVideo({ extension })) {
          return 'el-icon-video-camera'
        } else if (this.isAudio({ extension })) {
          return 'el-icon-headset'
        } else if (this.isPdf({ extension })) {
          return 'el-icon-document'
        } else if (this.isText({ extension })) {
          return 'el-icon-tickets'
        } else {
          return 'el-icon-document'
        }
      },
      
      // 格式化文件大小
      formatFileSize(size) {
        if (!size) return '0 B'
        
        const units = ['B', 'KB', 'MB', 'GB', 'TB']
        let fileSize = size
        let index = 0
        
        while (fileSize >= 1024 && index < units.length - 1) {
          fileSize /= 1024
          index++
        }
        
        return `${fileSize.toFixed(2)} ${units[index]}`
      },
      
      // 判断文件类型
      isImage(file) {
        const extension = file.extension ? file.extension.toLowerCase() : ''
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)
      },
      
      isVideo(file) {
        const extension = file.extension ? file.extension.toLowerCase() : ''
        return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(extension)
      },
      
      isAudio(file) {
        const extension = file.extension ? file.extension.toLowerCase() : ''
        return ['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)
      },
      
      isPdf(file) {
        const extension = file.extension ? file.extension.toLowerCase() : ''
        return extension === 'pdf'
      },
      
      isText(file) {
        const extension = file.extension ? file.extension.toLowerCase() : ''
        return ['txt', 'md', 'json', 'js', 'html', 'css', 'xml', 'java', 'py', 'c', 'cpp', 'h', 'php', 'sql', 'log'].includes(extension)
      },
      
      // 触发文件夹上传
      triggerFolderUpload() {
        this.$refs.folderUploadInput.click()
      },
      
      // 获取已完成文件数量
      getCompletedCount() {
        let count = 0;
        for (const fileName in this.uploadProgress) {
          if (this.uploadProgress[fileName].status === 'success') {
            count++;
          }
        }
        return count;
      },
      
      // 清空上传进度列表
      clearUploadProgress() {
        // 只清除已完成的文件进度
        const newProgress = {};
        for (const fileName in this.uploadProgress) {
          if (this.uploadProgress[fileName].status !== 'success') {
            newProgress[fileName] = this.uploadProgress[fileName];
          }
        }
        this.uploadProgress = newProgress;
        
        // 如果没有正在上传的文件，关闭进度条
        if (Object.keys(this.uploadProgress).length === 0) {
          this.showUploadProgress = false;
        }
      },
      
      // 处理文件夹上传
      async handleFolderUpload(event) {
        const files = event.target.files
        if (!files || files.length === 0) return
        
        this.uploadingFolder = true
        this.showUploadProgress=true;
        this.folderFiles = Array.from(files)
        this.totalFiles = this.folderFiles.length
        this.processedFiles = 0
        
        try {
          // 准备批量上传
          const uploadFiles = []
          const relativePaths = []
          console.log("this.folderFiles",this.folderFiles)
          // 收集所有文件和相对路径
          for (const file of this.folderFiles) {
            uploadFiles.push(file)
            relativePaths.push(file.webkitRelativePath)
          }
          // 批量上传文件
          // 使用Promise.all并行处理所有文件上传
          const uploadPromises = uploadFiles.map((file, index) => {
            this.$set(this.uploadProgress, relativePaths[index], {
              percent: 0,
              status: 'warning',
              parentId:this.currentFolderId,
              busId:this.busId,
              relativePath:relativePaths[index],
              file:file
            });
            console.log("uploadProgress",this.uploadProgress)
            return batchUploadFiles([file], this.currentFolderId, [relativePaths[index]], this.busId,(percent) => {
              this.$set(this.uploadProgress[relativePaths[index]], 'percent', percent)
            },this.path)
                .then(response => {
                  this.$set(this.uploadProgress[relativePaths[index]], 'status', 'success');
                  if (this.needLateBinding && response.code === 200) {
                    // 存储上传成功的文件ID，等待后续关联
                    return response.data;
                  }
                  return [];
                })
                .catch(error => {
                  this.$set(this.uploadProgress[relativePaths[index]], 'status', 'exception');
                  console.error(`上传文件 ${file.name} 失败:`, error);
                  return [];
                });
          });
          const results = await Promise.all(uploadPromises);
          // 合并所有返回的文件ID
          this.pendingFiles.push(...results.flat());
          console.log("pendingFiles",this.pendingFiles)
          this.$message.success('文件夹上传成功')
        } catch (error) {
          console.error('文件夹上传失败:', error)
          this.$message.error('文件夹上传失败: ' + (error.message || '未知错误'))
        } finally {
          this.uploadingFolder = false
          // 清空文件输入
          event.target.value = ''
          this.fetchFileList()
        }
      },

      // 重试上传文件
      retryUpload(fileName, process) {
        if (!process) {
          this.$message.error('文件引用丢失，无法重新上传');
          return;
        }
        // 重置进度
        this.$set(this.uploadProgress[fileName], 'percent', 0);
        this.$set(this.uploadProgress[fileName], 'status', 'warning');
        
        // 获取相对路径
        const relativePath = process.relativePath || '';
        if(relativePath){
          batchUploadFiles([process.file],process.parentId,[process.relativePath],process.busId,(percent) => {
            this.$set(this.uploadProgress[fileName], 'percent', percent)
          },this.path).then((response)=>{
            if (this.needLateBinding && response.code === 200) {
              // 存储上传成功的文件ID，等待后续关联
              this.pendingFiles.push(...response.data)
            }
            this.$set(this.uploadProgress[fileName], 'status', 'success');
          }).catch(error => {
            this.$set(this.uploadProgress[fileName], 'status', 'exception');
          });
        }else{
          uploadFile(process.file,process.parentId,process.busId,(percent) => {
            this.$set(this.uploadProgress[fileName], 'percent', percent)
          }).then((response)=>{
            if (this.needLateBinding && response.code === 200) {
              // 存储上传成功的文件ID，等待后续关联
              this.pendingFiles.push(...response.data)
            }
            this.$set(this.uploadProgress[fileName], 'status', 'success');
            this.fetchFileList()
          }).catch(error => {
            this.$set(this.uploadProgress[fileName], 'status', 'exception');
          });
        }
      },
      
      // 处理拖拽进入
      handleDragOver(event) {
        this.isDragOver = true
        event.dataTransfer.dropEffect = 'copy'
      },
      
      // 处理拖拽离开
      handleDragLeave(event) {
        // this.isDragOver = false
      },
      
      // 处理文件拖放
      async handleDrop(event) {
        this.isDragOver = false
        this.showUploadProgress=true;
        const items = event.dataTransfer.items
        if (!items || items.length === 0) return
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          if (item.webkitGetAsEntry && item.webkitGetAsEntry().isDirectory) {
             this.handleFolderDrop(item)
          }else if(item.webkitGetAsEntry().isFile){
            // 处理文件
            item.webkitGetAsEntry().file((file)=>{
              // 为每个文件创建进度记录
              this.$set(this.uploadProgress, file.name, {
                percent: 0,
                status: 'warning',
                file: file, // 保存文件引用，用于重试
                parentId:this.currentFolderId,
                busId:this.busId
              });
              uploadFile(file,this.currentFolderId,this.busId,(percent) => {
                this.$set(this.uploadProgress[file.name], 'percent', percent)
              },this.path).then((response)=>{
                if (this.needLateBinding && response.code === 200) {
                  // 存储上传成功的文件ID，等待后续关联
                  this.pendingFiles.push(...response.data)
                }
                this.$set(this.uploadProgress[file.name], 'status', 'success');
                this.fetchFileList()
              }).catch(()=>{
                this.$set(this.uploadProgress[file.name], 'status', 'exception');
              })
            })
          }
        }

      },
      
      // 处理文件拖放上传
      async handleFileDrop(files) {
        try {
          this.uploading = true
          const loadingInstance = this.$loading({
            lock: true,
            text: '正在上传文件...',
            spinner: 'el-icon-loading',
            background: 'rgba(0, 0, 0, 0.7)'
          })
          
          // 转换FileList为数组
          const fileArray = Array.from(files)
          
          // 上传文件
          for (const file of fileArray) {
            await uploadFile(file,this.currentFolderId,this.busId)
          }
          
          loadingInstance.close()
          this.$message.success('文件上传成功')
        } catch (error) {
          console.error('文件上传失败:', error)
          this.$message.error('文件上传失败: ' + (error.message || '未知错误'))
        } finally {
          this.uploading = false
        }
      },
      
      // 处理文件夹拖放上传
      async handleFolderDrop(item) {
        try {
          this.uploadingFolder = true
          // 收集所有文件和相对路径
          const uploadFiles = []
          const relativePaths = []
          
          // 递归处理文件夹
          const processEntry = async (entry, path = '') => {
            if (entry.isFile) {
              // 处理文件
              const file = await new Promise((resolve) => {
                entry.file(resolve)
              })
              uploadFiles.push(file)
              relativePaths.push(path + file.name)
            } else if (entry.isDirectory) {
              // 处理文件夹
              const dirPath = path + entry.name + '/'
              // 读取文件夹内容
              const reader = entry.createReader()
              const entries = await new Promise((resolve) => {
                reader.readEntries(resolve)
              })
              // 递归处理子项
              for (const childEntry of entries) {
                await processEntry(childEntry, dirPath)
              }
            }
          }
          const entry = item.webkitGetAsEntry()
          if (entry) {
            await processEntry(item.webkitGetAsEntry())
          }
          // 如果有文件需要上传
          if (uploadFiles.length > 0) {
            let rootFolder = { id: this.currentFolderId }
            // 批量上传文件
            //由于一次上传多个太慢了，分多次请求上传
            let uploadTask=[];
            for (let i = 0; i < uploadFiles.length; i++) {
              this.$set(this.uploadProgress, relativePaths[i], {
                percent: 0,
                status: 'warning',
                parentId:this.currentFolderId,
                busId:this.busId,
                relativePath:relativePaths[i],
                file:uploadFiles[i]
              });
              uploadTask.push( batchUploadFiles([uploadFiles[i]], rootFolder.id, [relativePaths[i]], this.busId,(percent) => {
                this.$set(this.uploadProgress[relativePaths[i]], 'percent', percent)
              },this.path).then((response)=>{
               if (this.needLateBinding && response.code === 200) {
                 // 存储上传成功的文件ID，等待后续关联
                 this.pendingFiles.push(...response.data)
               }
                this.$set(this.uploadProgress[relativePaths[i]], 'status', 'success');
             }).catch(()=>{
                this.$set(this.uploadProgress[relativePaths[i]], 'status', 'exception');
              }))
            }
            await Promise.all(uploadTask)
            console.log("pendingFiles",this.pendingFiles)
          }
        } catch (error) {
          console.error('文件夹上传失败:', error)
          this.$message.error('文件夹上传失败: ' + (error.message || '未知错误'))
        } finally {
          this.uploadingFolder = false
          this.fetchFileList()
        }
      },
      
      // 处理每页显示数量变化
      handleSizeChange(size) {
        this.pageSize = size
        this.pageNum = 1 // 重置到第一页
        this.fetchFileList()
      },
      
      // 处理页码变化
      handleCurrentChange(page) {
        this.pageNum = page
        this.fetchFileList()
      },
      
      // 提供给父组件调用的方法，用于更新业务ID
      async updateBusId(newBusId) {
        if (!newBusId || this.pendingFiles.length === 0) return
        
        try {
          // 批量更新文件的业务ID
          await batchUpdateBusId(this.pendingFiles, newBusId)
          
          // 清空待处理文件列表
          this.pendingFiles = []
          
          // 刷新文件列表
          this.busId = newBusId
          this.fetchFileList()
          
          return true
        } catch (error) {
          console.error('更新文件业务ID失败:', error)
          return false
        }
      },
      
      // 获取进度条状态
      getProgressStatus(status) {
        if (status === 'success') return 'success';
        if (status === 'error') return 'exception';
        return ''; // 默认状态
      },
      
      // 开始拖动
      startDrag(event) {
        // 如果点击的是按钮，不启动拖动
        if (event.target.closest('.el-button')) {
          return;
        }
        
        this.isDragging = true;
        this.isResizing = false; // 确保不是调整大小模式
        
        // 记录起始位置
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartLeft = this.progressLeft;
        this.dragStartTop = this.progressTop;
        
        event.preventDefault();
      },
      
      // 开始调整大小
      startResize(event) {
        this.isResizing = true;
        this.isDragging = false; // 确保不是拖动模式
        
        // 记录起始大小和位置
        this.resizeStartWidth = this.progressWidth;
        this.resizeStartHeight = this.progressHeight;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        
        event.preventDefault();
        event.stopPropagation(); // 防止触发拖动
      },
      
      // 鼠标移动处理
      onMouseMove(event) {
        if (this.isDragging) {
          // 计算位置偏移量
          const deltaX = event.clientX - this.dragStartX;
          const deltaY = event.clientY - this.dragStartY;
          
          // 计算新位置
          let newLeft = this.dragStartLeft + deltaX;
          let newTop = this.dragStartTop + deltaY;
          
          // 限制不超出窗口
          newLeft = Math.max(0, Math.min(window.innerWidth - this.progressWidth, newLeft));
          newTop = Math.max(0, Math.min(window.innerHeight - this.progressHeight, newTop));
          
          // 只更新位置，不改变大小
          this.progressLeft = newLeft;
          this.progressTop = newTop;
        } else if (this.isResizing) {
          // 计算大小偏移量
          const deltaX = event.clientX - this.dragStartX;
          const deltaY = event.clientY - this.dragStartY;
          
          // 计算新大小
          let newWidth = this.resizeStartWidth + deltaX;
          let newHeight = this.resizeStartHeight + deltaY;
          
          // 限制最小尺寸
          newWidth = Math.max(300, newWidth);
          newHeight = Math.max(200, newHeight);
          
          // 限制最大尺寸
          newWidth = Math.min(window.innerWidth - this.progressLeft, newWidth);
          newHeight = Math.min(window.innerHeight - this.progressTop, newHeight);
          
          // 只更新大小，不改变位置
          this.progressWidth = newWidth;
          this.progressHeight = newHeight;
        }
      },
      
      // 鼠标释放处理
      onMouseUp() {
        this.isDragging = false;
        this.isResizing = false;
      },
      
      // 切换最小化状态
      toggleMinimize() {
        this.isProgressMinimized = !this.isProgressMinimized;
        
        // 如果从最小化状态恢复，确保尺寸合理
        if (!this.isProgressMinimized) {
          // 恢复默认尺寸
          if (this.progressWidth < 300) this.progressWidth = 450;
          if (this.progressHeight < 200) this.progressHeight = 400;
        }
      },
      
      // 处理选择变化
      handleSelectionChange(selection) {
        this.selectedFiles = selection;
      },
      
      // 检查是否可选择
      checkSelectable(row) {
        // 允许选择文件和文件夹
        return true;
      },
      
      // 批量下载
      async batchDownload() {
        if (this.selectedFiles.length === 0) {
          this.$message.warning('请选择要下载的文件或文件夹');
          return;
        }
        
        try {
          const fileIds = this.selectedFiles.map(file => file.id);
          
          // 在新窗口打开下载链接
          const downloadUrl = `${process.env.VUE_APP_BASE_API2}/klgeek/file/batch-download`;
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = downloadUrl;
          form.target = '_blank'; // 在新窗口打开
          
          // 添加文件ID参数
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'fileIds';
          input.value = JSON.stringify(fileIds);
          form.appendChild(input);
          
          // 添加token
          const tokenInput = document.createElement('input');
          tokenInput.type = 'hidden';
          tokenInput.name = 'accessToken';
          tokenInput.value = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
          form.appendChild(tokenInput);
          
          document.body.appendChild(form);
          form.submit();
          document.body.removeChild(form);
          
          this.$message.success('正在准备下载文件...');
        } catch (error) {
          console.error('批量下载失败:', error);
          this.$message.error('批量下载失败: ' + (error.message || '未知错误'));
        }
      },
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .file-manager {
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    
    .file-manager-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      //border-bottom: 1px solid #ebeef5;
      
      .breadcrumb {
        flex: 1;
      }
      
      .actions {
        display: flex;
        gap: 10px;
      }
    }
    
    .file-list-container {
      position: relative;
      &.drag-over {
        border: 2px dashed #409EFF;
        background-color: rgba(64, 158, 255, 0.1);
      }
      .file-item {
        display: flex;
        align-items: center;

        i {
          font-size: 20px;
          margin-right: 8px;

          &.el-icon-folder {
            color: #e6a23c;
          }

          &.el-icon-document {
            color: #409eff;
          }

          &.el-icon-picture {
            color: #67c23a;
          }

          &.el-icon-video-camera {
            color: #f56c6c;
          }

          &.el-icon-headset {
            color: #909399;
          }

          &.el-icon-tickets {
            color: #9254de;
          }
        }

        .file-name {
          overflow: hidden;
          text-overflow: ellipsis;
          //white-space: nowrap;
          word-break: break-all;
        }
      }
      .drag-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.9);
        z-index: 10;
        display: flex;
        justify-content: center;
        align-items: center;
        
        .drag-tip {
          text-align: center;
          
          i {
            font-size: 48px;
            color: #409EFF;
          }
          
          p {
            margin-top: 16px;
            font-size: 16px;
            color: #606266;
          }
        }
      }
    }
    
    .pagination-container {
      margin-top: 20px;
      display: flex;
      justify-content: center;
    }
  }
  
  .upload-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 10px;
  }
  
  .preview-dialog {
    .preview-image {
      max-width: 100%;
      max-height: 500px;
      display: block;
      margin: 0 auto;
    }
    
    .preview-video, .preview-audio {
      width: 100%;
      max-height: 500px;
    }
    
    .preview-pdf {
      width: 100%;
      height: 500px;
    }
    
    .preview-text {
      width: 100%;
      height: 500px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      padding: 10px;
      overflow: auto;
      font-family: monospace;
      white-space: pre-wrap;
    }
    
    .preview-unsupported {
      text-align: center;
      padding: 50px 0;
      color: #909399;
    }
  }
  
  .delete-btn {
    color: #f56c6c;
  }
  
  // 移动端适配
  @media screen and (max-width: 768px) {
    .file-manager {
      .file-manager-header {
        flex-direction: column;
        align-items: flex-start;
        
        .breadcrumb {
          width: 100%;
          overflow-x: auto;
          white-space: nowrap;
        }
        
        .actions {
          width: 100%;
          justify-content: space-between;
        }
      }
      
      .file-list-container {
        padding: 10px;
      }
    }
    
    .el-table {
      ::v-deep .el-table__fixed-right {
        height: auto !important;
      }
    }
    
    .preview-dialog {
      .preview-image, .preview-video, .preview-audio, .preview-pdf, .preview-text {
        max-height: 300px;
      }
    }
  }
  
  .upload-progress-container {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    resize: none; /* 禁用浏览器原生的resize功能，使用自定义的 */
    transition: all 0.3s;
    
    &.minimized {
      width: auto !important;
      height: auto !important;
      border-radius: 50%;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      cursor: pointer;
      
      .progress-minimized {
        display: flex;
        align-items: center;
        justify-content: center;
      /*  width: 150px;
        height: 150px;*/
        position: relative;
        
        i {
          font-size: 24px;
          color: #409eff;
        }
        
        .progress-count {
          position: absolute;
          top: 0;
          right: 0;
          background-color: #f56c6c;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 12px;
          transform: translate(30%, -30%);
        }
      }
    }
  }
  
  .upload-progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    border-bottom: 1px solid #ebeef5;
    cursor: move; /* 指示可拖动 */
    user-select: none; /* 防止文本选择 */
    
    .progress-title {
      display: flex;
      flex-direction: column;
      
      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 500;
      }
      
      .progress-summary {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }
    
    .progress-actions {
      display: flex;
      align-items: center;
      
      .el-button {
        padding: 4px;
        margin-left: 8px;
      }
    }
  }
  
  .upload-progress-list {
    padding: 10px 15px;
    overflow-y: auto;
    flex: 1; /* 让列表区域自动填充剩余空间 */
  }
  
  .upload-progress-item {
    margin-bottom: 15px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .file-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 5px;
      
      .file-name {
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
      }
      
      .retry-button {
        padding: 2px;
        margin-left: 8px;
        color: #f56c6c;
        
        &:hover {
          color: #f78989;
        }
      }
    }
  }
  
  .resize-handle {
    position: absolute;
    width: 14px;
    height: 14px;
    background-color: #409eff;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
    
    &.resize-handle-se {
      bottom: 0;
      right: 0;
      cursor: se-resize;
      border-radius: 0 0 4px 0;
    }
  }
  </style>